import { useRateFieldError } from "../../../../hooks/useRateFieldError";
import { FC, useEffect } from "react";

import { Box, Stack } from "@mui/material";
import { browserDefaultTimezone } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { resolveScheduleDates } from "../../../../../helpers/resolveScheduleDates";
import { useRateFieldName } from "../../../../hooks/useRateFieldName";

import {
  SCHEDULE_TOOLTIP_SLOT_PROPS,
  scheduleDateFieldSx,
  scheduleRowItemSx,
  scheduleSelectSx,
} from "./scheduleFieldStyles";
import { useCampaignSalesDates } from "./useCampaignSalesDates";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardDateTimePicker } from "@/dashboard-design-system/DateTimePicker";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem/DashboardMenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select/DashboardSelect";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useStableLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface EndsOnFieldsProps {
  isSweepstakes?: boolean;
}

export const EndsOnFields: FC<EndsOnFieldsProps> = ({ isSweepstakes = false }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { control, setValue, getValues, unregister } = useFormContext<StepTicketsFormData>();
  const { apiFormObject: apiTicketing } = useStableLiveFormEditorContext<"ticketing">();

  const endModeField = useRateFieldName("salesEndMode");
  const endDateField = useRateFieldName("salesEndDate");
  const startModeField = useRateFieldName("salesStartMode");
  const startDateField = useRateFieldName("salesStartDate");
  const startAfterRateField = useRateFieldName("salesStartAfterRateId");

  const endMode = useWatch({ control, name: endModeField });
  const timezone = apiTicketing.eventTimezone || browserDefaultTimezone;
  const { hasCustomSalesCloseDate } = useCampaignSalesDates();

  const endDateError = useRateFieldError("salesEndDate");

  useEffect(() => {
    return () => {
      unregister(endDateField, { keepValue: true });
    };
  }, [endDateField, unregister]);

  const validateEndAfterStart = (value: unknown) => {
    if (!value) {
      return true;
    }
    const { startDate } = resolveScheduleDates(
      {
        isScheduled: true,
        salesStartMode: getValues(startModeField),
        salesStartDate: getValues(startDateField),
        salesStartAfterRateId: getValues(startAfterRateField),
        salesEndMode: "formDefault",
        salesEndDate: null,
      },
      getValues("rates") ?? []
    );
    if (!startDate) {
      return true;
    }
    return new Date(value as Date) > startDate || t("dashboard", "ticketing.schedule.endAfterStartError");
  };

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={0.5} alignItems={{ md: "flex-start" }}>
      <Box sx={scheduleRowItemSx}>
        <DashboardTypography variant="caption" vibe="text-neutral-quiet" sx={{ whiteSpace: "nowrap" }}>
          {t("dashboard", "ticketing.schedule.endsOn")}
        </DashboardTypography>
      </Box>
      <Box sx={{ width: 216, maxWidth: "100%", flexShrink: 0, minHeight: 30 }}>
        <DashboardSelect
          control={control}
          name={endModeField}
          onChange={() => setValue(endDateField, null, { shouldDirty: true })}
          size="small"
          sx={scheduleSelectSx}
          data-test="rate-schedule-ends-on-select"
        >
          <DashboardMenuItem value="formDefault">
            {t(
              "dashboard",
              hasCustomSalesCloseDate ? "ticketing.schedule.salesClosingDate" : "ticketing.schedule.eventEnd"
            )}
          </DashboardMenuItem>
          <DashboardMenuItem value="customDate">{t("dashboard", "ticketing.schedule.customDate")}</DashboardMenuItem>
        </DashboardSelect>
      </Box>
      {hasCustomSalesCloseDate && endMode === "formDefault" && (
        <DashboardInformationTooltip
          sx={scheduleRowItemSx}
          title={
            t(
              "dashboard",
              isSweepstakes ? "sweepstakes.rates.scheduleClosingTooltip" : "ticketing.schedule.salesClosingTooltip"
            ) as string
          }
          placement="bottom"
          slotProps={SCHEDULE_TOOLTIP_SLOT_PROPS}
        />
      )}
      {endMode === "customDate" && (
        <Box sx={scheduleDateFieldSx}>
          <DashboardDateTimePicker
            name={endDateField}
            control={control}
            locale={locale}
            rules={[
              isRequired(t("dashboard", "ticketing.schedule.dateRequired")),
              { validate: validateEndAfterStart },
              { deps: [startDateField, startAfterRateField] },
            ]}
            error={Boolean(endDateError?.message)}
            errorMessage={endDateError?.message}
            timezone={timezone}
            size="small"
          />
        </Box>
      )}
    </Stack>
  );
};
